import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ANGLES_D_OUVERTURE, FONDS_DE_PROJET, PAPIERS_DE_FICHE, TYPES_DE_FICHE } from '@nextilt/commun';
import { OngletCompte, OngletIABranchee, OngletMesDonnees } from '../comptes';
import { ApercuDeForme } from '../partage/ApercuDeForme';
import { EchantillonDePapier } from '../partage/EchantillonDePapier';
import { choisirLApparence, FORMES, useApparence, type Apparence, type Forme } from '../partage/apparence';
import { useAuPouce } from '../partage/surface';
import { useEmailConnecte } from '../partage/useArbre';
import {
  AMBIANCE,
  ANGLE_A_L_OUVERTURE,
  AMBIANCE_CARNET,
  AMBIANCE_CLAIR,
  AMBIANCE_SOMBRE,
  APERCU,
  BULLE_DE_L_APERCU,
  ECRITURE_A_LA_MAIN,
  EXPLICATION_DE_L_APERCU,
  EXPLICATION_DU_PROJET_NEUF,
  EXPLICATION_DU_STYLE,
  FORME_DES_BULLES,
  GRAIN_DU_PAPIER,
  APRES_UNE_BULLE_ECRITE,
  ECRIRE_LA_SUIVANTE,
  OUVRIR_SA_FICHE,
  LE_STYLE,
  MOTS_DES_ANGLES_D_OUVERTURE,
  MOTS_DES_FORMES_DE_PROJET,
  MOTS_DES_PAPIERS_DE_FICHE,
  MOTS_DES_TYPES_DE_FICHE,
  ONGLET_AIDE,
  ONGLET_APPARENCE,
  ONGLET_COMPTE,
  ONGLET_DONNEES,
  ONGLET_IA,
  ONGLET_PLUS_TARD,
  PAPIER_DE_LA_FICHE,
  PAPIER_FIXE_PAR_LE_TYPE,
  PAR_DEFAUT_UN_PROJET_NEUF,
  PARAMETRES,
  RELIEF_DOUX,
  RELIEF_ET_OMBRES,
  RELIEF_MARQUE,
  SOUS_TITRE_DES_PARAMETRES,
  TYPE_DE_FICHE_EN_ENTIER,
  FOND_DE_LA_CARTE,
  MOTS_DES_FONDS_DE_PROJET,
} from '../partage/vocabulaire';
import './Parametres.css';

/**
 * La page Paramètres de l'ordinateur (chantier apparence, modification 02, maquette
 * `design/parametres.html`), ouverte par la roue ⚙ de la barre du haut.
 *
 * Les onglets Apparence, Compte, IA branchée et Mes données s'ouvrent (les trois derniers depuis
 * le chantier parametres-du-compte, ticket 03 : leur contenu est au sujet comptes) ; Aide reste
 * marqué « Plus tard ». L'onglet ouvert est un état de la page, oublié en la quittant. Chaque
 * choix d'Apparence passe par `choisirLApparence` (`partage/apparence.ts`) : il est gardé sur
 * l'appareil et s'applique tout de suite, sans recharger.
 *
 * Au téléphone, ses Paramètres à lui s'ouvrent par sa roue ⚙ (`telephone/CadreDuTelephone.tsx`) : cette
 * adresse y ramène à Maintenant.
 */
export function Parametres() {
  const auPouce = useAuPouce();
  const apparence = useApparence();
  const email = useEmailConnecte();
  const [ouvert, setOuvert] = useState<Onglet>('apparence');
  if (auPouce) return <Navigate to="/maintenant" replace />;
  const choisi = ONGLETS.find((o) => o.cle === ouvert) ?? ONGLETS[0]!;

  return (
    <section className="parametres">
      <header className="parametres-tete">
        <h1>{PARAMETRES}</h1>
        <p>{SOUS_TITRE_DES_PARAMETRES}</p>
      </header>

      <div className="parametres-onglets" role="tablist" aria-label={PARAMETRES}>
        {ONGLETS.map(({ cle, nom, Icone }) => (
          <button
            key={cle}
            type="button"
            role="tab"
            aria-selected={cle === ouvert}
            data-onglet={cle}
            className={cle === ouvert ? 'est-choisi' : ''}
            onClick={() => setOuvert(cle)}
          >
            <Icone />
            {nom}
          </button>
        ))}
        {ONGLETS_PLUS_TARD.map(({ nom, Icone }) => (
          <button key={nom} type="button" role="tab" aria-selected="false" aria-disabled="true" disabled>
            <Icone />
            {nom}
            <span className="parametres-plus-tard">{ONGLET_PLUS_TARD}</span>
          </button>
        ))}
      </div>

      <div className="parametres-classeur" role="tabpanel" aria-label={choisi.nom}>
        {/*
         * Compte, IA branchée, Mes données : une feuille chacun, dans la première colonne du même
         * classeur, à la taille des feuilles de la maquette (les trois du bas, en pâle).
         */}
        {ouvert === 'compte' && (
          <FeuilleDuCompte titre={ONGLET_COMPTE}>
            <OngletCompte email={email} />
          </FeuilleDuCompte>
        )}
        {ouvert === 'ia' && (
          <FeuilleDuCompte titre={ONGLET_IA}>
            <OngletIABranchee />
          </FeuilleDuCompte>
        )}
        {ouvert === 'donnees' && (
          <FeuilleDuCompte titre={ONGLET_DONNEES}>
            <OngletMesDonnees email={email} />
          </FeuilleDuCompte>
        )}
        {ouvert === 'apparence' && (
          <div className="parametres-colonnes">
            <section className="parametres-bloc">
              <h2>{LE_STYLE}</h2>
              <p className="parametres-explication">{EXPLICATION_DU_STYLE}</p>
              <Choix
                nom={AMBIANCE}
                valeur={apparence.ambiance}
                options={[
                  ['carnet', AMBIANCE_CARNET],
                  ['clair', AMBIANCE_CLAIR],
                  ['sombre', AMBIANCE_SOMBRE],
                ]}
                onChoisir={(ambiance) => choisirLApparence({ ambiance })}
              />
              <OuiNon
                nom={ECRITURE_A_LA_MAIN}
                valeur={apparence.ecriture}
                onChoisir={(ecriture) => choisirLApparence({ ecriture })}
              />
              <Choix
                nom={RELIEF_ET_OMBRES}
                valeur={apparence.relief}
                options={[
                  ['doux', RELIEF_DOUX],
                  ['marque', RELIEF_MARQUE],
                ]}
                onChoisir={(relief) => choisirLApparence({ relief })}
              />
              <OuiNon
                nom={GRAIN_DU_PAPIER}
                valeur={apparence.grain}
                onChoisir={(grain) => choisirLApparence({ grain })}
              />
              {/* Ce que fait Entrée dans la barre « + Bulle » (modes-de-la-carte, 04) : gardé sur l'appareil. */}
              <Choix
                nom={APRES_UNE_BULLE_ECRITE}
                valeur={apparence.apresUneBulle}
                options={[
                  ['suivante', ECRIRE_LA_SUIVANTE],
                  ['fiche', OUVRIR_SA_FICHE],
                ]}
                onChoisir={(apresUneBulle) => choisirLApparence({ apresUneBulle })}
              />
            </section>

            {/*
             * Ce qu'un projet neuf reçoit (maquette du carnet `parametres.html`, « Par défaut, un
             * projet neuf »), en lignes compactes comme la maquette : la forme de ses bulles (la
             * choisie en petit, les huit dépliées dessous d'un clic), le fond de sa carte (chantier
             * projet-neuf), le type et le papier de ses fiches (chantier types-de-fiche, modification
             * 06), l'angle sur lequel il s'ouvre (chantier angle-a-l-ouverture, modification 02).
             */}
            <section className="parametres-bloc parametres-bloc--projet-neuf">
              <h2>{PAR_DEFAUT_UN_PROJET_NEUF}</h2>
              <p className="parametres-explication">{EXPLICATION_DU_PROJET_NEUF}</p>
              <FormeDUnProjetNeuf forme={apparence.forme} />
              <div className="parametres-ligne">
                <span>{FOND_DE_LA_CARTE}</span>
                <div className="parametres-papiers" role="radiogroup" aria-label={FOND_DE_LA_CARTE}>
                  {FONDS_DE_PROJET.map((fond) => (
                    <button
                      key={fond}
                      type="button"
                      role="radio"
                      aria-checked={fond === apparence.fondDeProjet}
                      aria-label={MOTS_DES_FONDS_DE_PROJET[fond]}
                      title={MOTS_DES_FONDS_DE_PROJET[fond]}
                      data-valeur={fond}
                      className={`parametres-papier${fond === apparence.fondDeProjet ? ' est-choisi' : ''}`}
                      onClick={() => choisirLApparence({ fondDeProjet: fond })}
                    >
                      <EchantillonDePapier papier={fond === 'uni' ? 'blanche' : fond} grand />
                    </button>
                  ))}
                </div>
              </div>
              <Choix
                nom={TYPE_DE_FICHE_EN_ENTIER}
                valeur={apparence.typeDeFiche}
                options={TYPES_DE_FICHE.map((t) => [t, MOTS_DES_TYPES_DE_FICHE[t]])}
                onChoisir={(typeDeFiche) => choisirLApparence({ typeDeFiche })}
              />
              <div className="parametres-ligne">
                <span title={PAPIER_FIXE_PAR_LE_TYPE}>{PAPIER_DE_LA_FICHE}</span>
                <div className="parametres-papiers" role="radiogroup" aria-label={PAPIER_DE_LA_FICHE}>
                  {PAPIERS_DE_FICHE.map((papier) => (
                    <button
                      key={papier}
                      type="button"
                      role="radio"
                      aria-checked={papier === apparence.papierDeFiche}
                      aria-label={MOTS_DES_PAPIERS_DE_FICHE[papier]}
                      title={MOTS_DES_PAPIERS_DE_FICHE[papier]}
                      data-valeur={papier}
                      className={`parametres-papier${papier === apparence.papierDeFiche ? ' est-choisi' : ''}`}
                      onClick={() => choisirLApparence({ papierDeFiche: papier })}
                    >
                      <EchantillonDePapier papier={papier} grand />
                    </button>
                  ))}
                </div>
              </div>
              <Choix
                nom={ANGLE_A_L_OUVERTURE}
                valeur={apparence.angleALOuverture}
                options={ANGLES_D_OUVERTURE.map((a) => [a, MOTS_DES_ANGLES_D_OUVERTURE[a]])}
                onChoisir={(angleALOuverture) => choisirLApparence({ angleALOuverture })}
              />
            </section>

            <section className="parametres-bloc">
              <h2>{APERCU}</h2>
              <p className="parametres-explication">{EXPLICATION_DE_L_APERCU}</p>
              <Apercu apparence={apparence} />
            </section>
          </div>
        )}
      </div>
    </section>
  );
}

/** Une feuille d'un onglet du compte (maquette `.fp`), sans explication sous son titre, comme la maquette. */
function FeuilleDuCompte({ titre, children }: { titre: string; children: React.ReactNode }) {
  return (
    <div className="parametres-colonnes">
      <section className="parametres-bloc parametres-bloc--compte">
        <h2>{titre}</h2>
        {children}
      </section>
    </div>
  );
}

/**
 * La forme des bulles d'un projet neuf, en une ligne comme la maquette (« ◯ Galet ») : la forme
 * choisie dessinée en petit, avec son nom. Un clic sur la ligne déplie dessous les huit formes,
 * un choix les replie (demande d'Igor du 2026-10-21). Ce qui est déplié est un état de l'écran.
 */
function FormeDUnProjetNeuf({ forme }: { forme: Forme }) {
  const [deplie, setDeplie] = useState(false);
  return (
    <>
      <button
        type="button"
        className="parametres-ligne parametres-ligne--depliable"
        aria-expanded={deplie}
        onClick={() => setDeplie(!deplie)}
      >
        <span>{FORME_DES_BULLES}</span>
        <span className="parametres-forme-choisie" data-valeur={forme}>
          <ApercuDeForme forme={forme} />
          {MOTS_DES_FORMES_DE_PROJET[forme]}
        </span>
      </button>
      {deplie && (
        <div className="parametres-formes" role="radiogroup" aria-label={FORME_DES_BULLES}>
          {FORMES.map((f) => (
            <button
              key={f}
              type="button"
              role="radio"
              aria-checked={f === forme}
              data-valeur={f}
              className={`parametres-forme${f === forme ? ' est-choisi' : ''}`}
              onClick={() => {
                choisirLApparence({ forme: f });
                setDeplie(false);
              }}
            >
              <ApercuDeForme forme={f} />
              {MOTS_DES_FORMES_DE_PROJET[f]}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/** Une ligne de réglage à plusieurs choix, l'un d'eux choisi. */
function Choix<T extends string>({
  nom,
  valeur,
  options,
  onChoisir,
}: {
  nom: string;
  valeur: T;
  options: [T, string][];
  onChoisir: (valeur: T) => void;
}) {
  return (
    <div className="parametres-ligne">
      <span>{nom}</span>
      <div className="parametres-choix" role="radiogroup" aria-label={nom}>
        {options.map(([option, mot]) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={option === valeur}
            className={option === valeur ? 'est-choisi' : ''}
            onClick={() => onChoisir(option)}
          >
            {mot}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Une ligne de réglage oui ou non : un interrupteur. */
function OuiNon({ nom, valeur, onChoisir }: { nom: string; valeur: boolean; onChoisir: (valeur: boolean) => void }) {
  return (
    <div className="parametres-ligne">
      <span>{nom}</span>
      <button
        type="button"
        role="switch"
        aria-checked={valeur}
        aria-label={nom}
        className={`parametres-interrupteur${valeur ? ' est-choisi' : ''}`}
        onClick={() => onChoisir(!valeur)}
      />
    </div>
  );
}

/**
 * Une bulle dans la forme et l'ambiance choisies. L'ambiance lui vient des jetons, que la racine
 * de la page redonne (`ApparenceDeLaPage`) ; la forme, de celle d'un projet neuf, dessinée par les
 * feuilles des bulles (`ApercuDeForme`).
 */
function Apercu({ apparence }: { apparence: Apparence }) {
  return (
    <div className="parametres-apercu" aria-hidden="true">
      <ApercuDeForme forme={apparence.forme} grand />
      <span className="parametres-apercu-titre">{BULLE_DE_L_APERCU}</span>
    </div>
  );
}

/* Les icônes des onglets, dessinées comme celles de la maquette. */
function Icone({ children }: { children: React.ReactNode }) {
  return (
    <svg
      className="parametres-icone"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function IconeApparence() {
  return (
    <Icone>
      <circle cx="12" cy="12" r="8" />
      <circle cx="9" cy="9" r="1.2" />
      <circle cx="15" cy="9" r="1.2" />
      <circle cx="8" cy="14" r="1.2" />
    </Icone>
  );
}

type Onglet = 'apparence' | 'compte' | 'ia' | 'donnees';

/** Les onglets qui s'ouvrent, dans l'ordre de la maquette. */
const ONGLETS: { cle: Onglet; nom: string; Icone: () => React.ReactElement }[] = [
  { cle: 'apparence', nom: ONGLET_APPARENCE, Icone: IconeApparence },
  {
    cle: 'compte',
    nom: ONGLET_COMPTE,
    Icone: () => (
      <Icone>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c1-4 4-6 8-6s7 2 8 6" />
      </Icone>
    ),
  },
  {
    cle: 'ia',
    nom: ONGLET_IA,
    Icone: () => (
      <Icone>
        <path d="M13 3 5 14h6l-1 7 8-11h-6z" />
      </Icone>
    ),
  },
  {
    cle: 'donnees',
    nom: ONGLET_DONNEES,
    Icone: () => (
      <Icone>
        <path d="M6 3h9l4 4v14H6z" />
        <path d="M15 3v4h4" />
      </Icone>
    ),
  },
];

/** L'onglet Aide reste « Plus tard » (décision 3 d'Igor du 2026-10-20 : l'Aide se refait à part). */
const ONGLETS_PLUS_TARD: { nom: string; Icone: () => React.ReactElement }[] = [
  {
    nom: ONGLET_AIDE,
    Icone: () => (
      <Icone>
        <path d="M5 4h14v11l-5 5H5z" />
        <path d="M14 20v-5h5" />
      </Icone>
    ),
  },
];
